<script setup lang="ts">
import { computed } from 'vue';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useClientCopyComposable } from '@/components/modal/clientCopy/useClientCopyComposable';
import { useModalComposable } from '@/views/composable';
import InputText from 'primevue/inputtext';

const props = defineProps<{
  airClone: any;
  index: number;
  isEditPricing: any;
  lang: string;
  currency: string;
  isEdit: boolean;
  isPriceValid: boolean;
  priceField: string;
}>();

const emits = defineEmits<{
  (e: 'update:isEditPricing', value: any): void;
  (e: 'update:isEdit', value: any): void;
  (e: 'update:isPriceValid', value: any): void;
}>();

const isEdit = computed({
  get() {
    return props.isEdit;
  },
  set(value) {
    emits('update:isEdit', value);
  }
});

const isPriceValid = computed({
  get() {
    return props.isPriceValid;
  },
  set(value) {
    emits('update:isPriceValid', value);
  }
});

const isEditPricing = computed({
  get() {
    return props.isEditPricing;
  },
  set(value) {
    emits('update:isEditPricing', value);
  }
});

const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(props.lang);
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { formatCurrency } = useClientCopyComposable(props.lang, props.currency);
const { handleOpenErrorMessage } = useModalComposable();
const headers = [$t('client_copy.adult_fare'), $t('client_copy.adult_taxes'), $t('client_copy.child_fare'), $t('client_copy.child_taxes'), $t('client_copy.infant_fare'), $t('client_copy.infant_taxes'), $t('client_copy.baggage_details')];

const formatFareBasic = (price: any) => (price.numberOfPassengers ? `- ${(price.fareBasis?.split(' / ') ?? []).map((part: string) => part.charAt(0)).join('/')} - ${price.numberOfPassengers} ${$t('common.pax').toLowerCase()}` : '');

const changeEditPricing = (index: number, indexPricing: number, status: boolean) => {
  isEdit.value = status;
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isEditPricing.value[index][indexPricing] = status;
};

const handleShowAdultChildren = (cruise: any, pricing: any) => {
  const adults = pricing.type.toUpperCase() === t('worksheet.economy_class').toUpperCase() ? cruise?.flights?.[0]?.adt : cruise?.flights?.[0]?.businessAdt;
  const children = pricing.type.toUpperCase() === t('worksheet.economy_class').toUpperCase() ? cruise?.flights?.[0]?.chd : cruise?.flights?.[0]?.businessChd;
  const enoughAdults = `${adults} ${t('common.adults')}`;
  const enoughChildren = `${children} ${t('common.children')}`;
  return `${pricing.type} (${enoughAdults}, ${enoughChildren})`;
};

const vCheckPrice = {
  mounted(element: any) {
    element.setAttribute('data-price', element.value.replace(/[^\d.-]/g, ''));
    element.addEventListener('blur', () => {
      const oldValuePrice = element.getAttribute('data-price');
      const newValuePrice = element.value.replace(/[^\d.-]/g, '');
      if (parseFloat(newValuePrice) < parseFloat(oldValuePrice)) {
        element.classList.add('p-invalid');
        isPriceValid.value = false;
      }
    });
  },
  updated(element: any) {
    const oldValueElement = element.getAttribute('data-price');
    const newValueElement = element.value.replace(/[^\d.-]/g, '');
    if (parseFloat(newValueElement) > parseFloat(oldValueElement)) {
      element.classList.remove('p-invalid');
      isPriceValid.value = true;
    }
  }
};
</script>

<template>
  <table class="table-child w-full">
    <thead>
      <tr>
        <th class="hidden" v-for="time in 9" :key="time"></th>
      </tr>
    </thead>
    <tbody>
      <tr class="background-grey text-sm">
        <td class="text-sm w-2">{{ $t('client_copy.class_type') }}</td>
        <td v-for="header in headers" :key="header" class="text-sm text-center">{{ header }}</td>
        <td class="text-sm text-center" v-if="authorizations?.my_quote?.client_copy_edit">{{ $t('common.action') }}</td>
      </tr>
      <tr class="text-sm" v-for="(pricing, indexPricing) in airClone[index][priceField]" :key="indexPricing">
        <td v-if="!isEditPricing[index][indexPricing] && priceField === 'price'" class="text-sm text-center">{{ pricing.type }} {{ formatFareBasic(pricing) }}</td>
        <td v-if="!isEditPricing[index][indexPricing] && priceField === 'flightPrices'" class="text-sm no-wrap">{{ handleShowAdultChildren(airClone[index], pricing) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.adt) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.adtTax) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.chd) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.chdTax) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.inf) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ formatCurrency(pricing.infTax) }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center">{{ pricing.detail }}</td>
        <td v-if="!isEditPricing[index][indexPricing]" class="text-sm text-center w-1">
          <i v-if="authorizations?.my_quote?.client_copy_edit" class="pi pi-pencil cursor-pointer p-1" @click="changeEditPricing(index, indexPricing, true)"></i>
        </td>
        <td v-if="isEditPricing[index][indexPricing]">
          <InputText v-model="pricing.type" focus type="text" class="text-center width-full" />
        </td>
        <td v-show="isEditPricing[index][indexPricing]">
          <InputText v-check-price v-model="pricing.adt" type="number" class="text-center width-full" />
        </td>
        <td v-show="isEditPricing[index][indexPricing]" class="text-sm text-center">
          {{ formatCurrency(pricing.adtTax) }}
        </td>
        <td v-show="isEditPricing[index][indexPricing]">
          <InputText v-check-price v-model="pricing.chd" type="number" class="text-center width-full" />
        </td>
        <td v-show="isEditPricing[index][indexPricing]" class="text-sm text-center">
          {{ formatCurrency(pricing.chdTax) }}
        </td>
        <td v-show="isEditPricing[index][indexPricing]">
          <InputText v-check-price v-model="pricing.inf" type="number" class="text-center width-full" />
        </td>
        <td v-show="isEditPricing[index][indexPricing]">
          {{ formatCurrency(pricing.infTax) }}
        </td>
        <td v-show="isEditPricing[index][indexPricing]">
          <InputText v-model="pricing.detail" type="text" class="text-center width-full" />
        </td>
        <td v-show="isEditPricing[index][indexPricing]" class="text-center w-1">
          <i class="pi pi-check text-cyan-500 cursor-pointer p-1" @click="changeEditPricing(index, indexPricing, false)"></i>
        </td>
      </tr>
    </tbody>
  </table>
</template>
